import React from 'react';
import { useQuery } from 'react-query';
import { Redirect } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getVendorProfile } from '../services/vendorService';
import { getOrganizerProfile } from '../services/eventService';
import { CircularProgress, Container, Box } from '@mui/material';

const ProfileRequiredRoute = ({ children }) => {
  const { currentUser } = useAuth();
  
  const roleId = currentUser ? Number(currentUser.role_id) : null;
  const isVendor = roleId === 1;
  const isOrganizer = roleId === 2;
  
  // Fetch profile based on role - hooks must be called unconditionally
  const { data: vendorProfile, isLoading: vendorLoading, error: vendorError } = useQuery(
    ['vendorProfile'],
    getVendorProfile,
    {
      enabled: isVendor && !!currentUser && currentUser.email_verified !== false,
      retry: false,
    }
  );
  
  const { data: organizerProfile, isLoading: organizerLoading, error: organizerError } = useQuery(
    ['organizerProfile'],
    getOrganizerProfile,
    {
      enabled: isOrganizer && !!currentUser && currentUser.email_verified !== false,
      retry: false,
    }
  );
  
  // Now handle conditional logic after hooks are called
  if (!currentUser) {
    return <Redirect to="/login" />;
  }
  
  // Check email verification first
  if (currentUser.email_verified === false) {
    return <Redirect to="/email-verification-required" />;
  }
  
  // Show loading state
  if ((isVendor && vendorLoading) || (isOrganizer && organizerLoading)) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  // Check if profile exists
  const hasProfile = isVendor ? !!vendorProfile : isOrganizer ? !!organizerProfile : false;
  const profileError = isVendor ? vendorError : organizerError;
  
  // If no profile and not a 404 error (which means profile doesn't exist), show error
  if (!hasProfile && profileError) {
    // 404 means profile doesn't exist - redirect to create profile
    if (profileError.response?.status === 404) {
      if (isVendor) {
        return <Redirect to="/create-vendor-profile" />;
      } else if (isOrganizer) {
        return <Redirect to="/create-organizer-profile" />;
      }
    }
  }
  
  // If no profile exists, redirect to create profile
  if (!hasProfile) {
    if (isVendor) {
      return <Redirect to="/create-vendor-profile" />;
    } else if (isOrganizer) {
      return <Redirect to="/create-organizer-profile" />;
    }
  }
  
  // Profile exists, allow access
  return children;
};

export default ProfileRequiredRoute;

